import { HomeRegistrationFragment } from "../../api/generated"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import useTipStatus from "../../tips/useTipStatus"
import { canEditLibrary } from "../../user/permissions"
import { HomeSetAsidePathsTipContainer } from "./HomeSetAsidePathsTip"
import HomeWelcomeAllTransitionalBlankSlateTip from "./HomeWelcomeAllTransitionalBlankSlateTip"
import HomeWelcomeDesignerBlankSlateTip from "./HomeWelcomeDesignerBlankSlateTip"
import HomeWelcomeMemberBlankSlateTip from "./HomeWelcomeMemberBlankSlateTip"

// Adjust as needed. Use to compare registration creation date to launch date.
const HARD_CODED_UNIFIED_HOME_LAUNCH_DATE = new Date("2025-08-28")

// // Registrations for Cohorts created by the user.
// export function filterCreatedPaths(registrations: HomeRegistrationFragment[]) {
//   return registrations.filter(
//     (registration) =>
//       registration.group?.created_by_id === registration.user?.id
//   )
// }

// Registrations for resources which weren't copied via config (indicating they were
// created within the school).
export function filterJoinedPaths(registrations: HomeRegistrationFragment[]) {
  return registrations.filter(
    (registration) => !registration.resource?.copied_with_copy_config_id
  )
}

function useHomeWelcomeTransitionalBlankSlateTip({
  registrations
}: UseHomeTipProps) {
  const tipId = "home-welcome-all-transitional-blank-slate"
  const [tipStatus] = useTipStatus(tipId)

  // Determine if user has joined a Path prior to Unified Home launch date,
  // indicating that they are transitioning to new Home screen.
  const hasJoinedAPathPreLaunch = filterJoinedPaths(registrations).some(
    (registration) =>
      new Date(registration.created).getTime() <
      HARD_CODED_UNIFIED_HOME_LAUNCH_DATE.getTime()
  )

  const showTip = tipStatus !== "dismissed" && hasJoinedAPathPreLaunch

  return showTip ? (
    <HomeWelcomeAllTransitionalBlankSlateTip key={tipId} />
  ) : null
}

function useHomeWelcomeDesignerBlankSlateTip({
  registrations
}: UseHomeTipProps) {
  const tipId = "home-welcome-designer-blank-slate"
  const pwContext = usePathwrightContext()
  const [tipStatus] = useTipStatus(tipId)

  // Only allow user to dismiss tip if they have a Path.
  // This effectively makes the tip act as a blank slate when they have no
  // Paths on their Home screen
  const layout = registrations.length ? "dismissable" : "default"

  const showTip =
    (tipStatus !== "dismissed" || layout === "default") &&
    canEditLibrary(pwContext)

  return showTip ? (
    <HomeWelcomeDesignerBlankSlateTip key={tipId} layout={layout} />
  ) : null
}

function useHomeWelcomeMemberBlankSlateTip({ registrations }: UseHomeTipProps) {
  const tipId = "home-welcome-member-blank-slate"
  const pwContext = usePathwrightContext()
  const [tipStatus] = useTipStatus(tipId)

  // Only allow user to dismiss tip if they have a Path.
  // This effectively makes the tip act as a blank slate when they have no
  // Paths on their Home screen
  const layout = registrations.length ? "dismissable" : "default"

  const showTip =
    (tipStatus !== "dismissed" || layout === "default") &&
    !canEditLibrary(pwContext)

  return showTip ? (
    <HomeWelcomeMemberBlankSlateTip key={tipId} layout={layout} />
  ) : null
}

function useHomeSetAsidePathsTip({ registrations }: UseHomeTipProps) {
  const tipId = "home-set-aside-paths"
  const [tipStatus] = useTipStatus(tipId)

  const showTip = tipStatus !== "dismissed"

  return showTip ? (
    <HomeSetAsidePathsTipContainer key={tipId} registrations={registrations} />
  ) : null
}

type UseHomeTipProps = {
  registrations: HomeRegistrationFragment[]
}

// Returns a single tip, first one that should be shown to the user (order matters).
export function useHomeTip({ registrations }: UseHomeTipProps) {
  const tips = [
    useHomeWelcomeTransitionalBlankSlateTip({ registrations }),
    useHomeWelcomeDesignerBlankSlateTip({ registrations }),
    useHomeWelcomeMemberBlankSlateTip({ registrations }),
    useHomeSetAsidePathsTip({ registrations })
  ]

  const tip = tips.find(Boolean)

  return tip
}

function HomeTips({ registrations }: UseHomeTipProps) {
  return useHomeTip({ registrations })
}

export default HomeTips
